import { useEffect, useMemo, useState } from "react";
import { normalizeForSearch } from "../../shared/components/Rankings/searchUtils";
import { calculateKtcStyleAdjustedTotals } from "../utils/dynastyTradeCalculator";
import {
  isTradeablePlayerAsset,
  type DynastyRosterTeam,
  type FindTradeRecommendation,
  type PlayerLookupResult,
} from "../features/shared/dynastyTradingPageUtils";

type FindTradeDirection = "up" | "down";
type FindTradeTargetPosition = "QB" | "RB" | "WR" | "TE" | "";

type UseDynastyFindTradeArgs = {
  importedTeams: DynastyRosterTeam[];
  isNarrowViewport: boolean;
  overallMaxPlayerValue: number;
  playerLookupQuery: string;
  resetKey: number;
  singleOpenTeam: DynastyRosterTeam | null;
};

function sumAssetValues<T extends { value: number | null }>(
  assets: T[],
  getValue?: (asset: T) => number | null
) {
  return assets.reduce((sum, asset) => sum + ((getValue ? getValue(asset) : asset.value) ?? 0), 0);
}

function calculateRecommendationDisplaySummary(
  receiveAssets: Array<{ value: number | null }>,
  sendAssets: Array<{ value: number | null }>,
  overallMaxPlayerValue: number
) {
  const comparisonSummary = calculateKtcStyleAdjustedTotals(
    receiveAssets,
    sendAssets,
    undefined,
    overallMaxPlayerValue
  );
  const receiveTopValue = Math.max(0, ...receiveAssets.map((asset) => Math.max(0, asset.value ?? 0)));
  const sendTopValue = Math.max(0, ...sendAssets.map((asset) => Math.max(0, asset.value ?? 0)));
  const premiumSide = receiveTopValue >= sendTopValue ? "receive" as const : "send" as const;
  const rawWinnerSide = comparisonSummary.favoredSide;
  const premiumTopValue = premiumSide === "receive" ? receiveTopValue : sendTopValue;
  const valueAdjustment =
    premiumSide === rawWinnerSide
      ? comparisonSummary.valueAdjustment
      : Math.round(comparisonSummary.valueAdjustment * (premiumTopValue / Math.max(1, overallMaxPlayerValue)));
  const receiveDisplayTotal = comparisonSummary.actualReceiveValue + (premiumSide === "receive" ? valueAdjustment : 0);
  const sendDisplayTotal = comparisonSummary.actualSendValue + (premiumSide === "send" ? valueAdjustment : 0);

  return {
    receiveDisplayTotal,
    sendDisplayTotal,
  };
}

function findRecommendationBalanceAddValue(
  receiveAssets: Array<{ value: number | null }>,
  sendAssets: Array<{ value: number | null }>,
  overallMaxPlayerValue: number
) {
  const initialSummary = calculateRecommendationDisplaySummary(receiveAssets, sendAssets, overallMaxPlayerValue);
  return Math.round(Math.abs(initialSummary.receiveDisplayTotal - initialSummary.sendDisplayTotal));
}

export function useDynastyFindTrade(args: UseDynastyFindTradeArgs) {
  const { importedTeams, isNarrowViewport, overallMaxPlayerValue, playerLookupQuery, resetKey, singleOpenTeam } =
    args;
  const [findTradePlayerId, setFindTradePlayerId] = useState<string | null>(null);
  const [findTradeDirection, setFindTradeDirection] = useState<FindTradeDirection>("up");
  const [findTradeTargetPosition, setFindTradeTargetPosition] = useState<FindTradeTargetPosition>("");
  const [findTradeTargetTeamId, setFindTradeTargetTeamId] = useState<string>("");
  const [visibleFindTradeCount, setVisibleFindTradeCount] = useState(10);
  const [mobileFindTradeQuery, setMobileFindTradeQuery] = useState("");

  const selectedFindTradeTeam = useMemo(() => {
    if (!findTradePlayerId) return null;
    return importedTeams.find((team) => team.players.some((player) => player.id === findTradePlayerId)) ?? null;
  }, [findTradePlayerId, importedTeams]);

  const selectedFindTradePlayer = useMemo(() => {
    if (!findTradePlayerId) return null;
    const sourceTeam = isNarrowViewport ? selectedFindTradeTeam : singleOpenTeam;
    if (!sourceTeam) return null;
    return sourceTeam.players.find((player) => player.id === findTradePlayerId) ?? null;
  }, [findTradePlayerId, isNarrowViewport, selectedFindTradeTeam, singleOpenTeam]);

  const playerLookupResults = useMemo(() => {
    const query = normalizeForSearch(playerLookupQuery);
    if (!query) return [];

    const matches: PlayerLookupResult[] = [];
    importedTeams.forEach((team) => {
      team.players.forEach((player) => {
        const searchable = normalizeForSearch(`${player.name} ${player.position} ${player.team}`);
        if (!searchable.includes(query)) return;
        matches.push({ team, player });
      });
    });

    return matches
      .sort((left, right) => {
        const valueGap =
          (right.player.value ?? Number.NEGATIVE_INFINITY) - (left.player.value ?? Number.NEGATIVE_INFINITY);
        if (valueGap !== 0) return valueGap;
        return left.player.name.localeCompare(right.player.name);
      })
      .slice(0, 8);
  }, [importedTeams, playerLookupQuery]);

  const findTradeRecommendations = useMemo(() => {
    const sourceTeam = isNarrowViewport ? selectedFindTradeTeam : singleOpenTeam;
    if (
      !sourceTeam ||
      !selectedFindTradePlayer ||
      !findTradeTargetPosition ||
      !isTradeablePlayerAsset(selectedFindTradePlayer)
    ) {
      return [];
    }

    const recommendations: FindTradeRecommendation[] = [];
    const selectedPlayerValue = selectedFindTradePlayer.value ?? 0;
    const minimumRecommendedPieceValue = selectedPlayerValue <= 3000 ? 0 : 2000;
    const restrictedTargetTeamId = Number(findTradeTargetTeamId || 0);
    const isSpecificTargetTeam = Boolean(restrictedTargetTeamId);
    const isEliteSelectedPlayer = selectedPlayerValue >= 8000;
    const myTeamAssets = sourceTeam.players.filter(
      (player) =>
        isTradeablePlayerAsset(player) &&
        player.id !== selectedFindTradePlayer.id &&
        (player.value ?? 0) >=
          (isSpecificTargetTeam ? Math.max(0, minimumRecommendedPieceValue - 500) : minimumRecommendedPieceValue)
    );

    for (const partnerTeam of importedTeams) {
      if (partnerTeam.rosterId === sourceTeam.rosterId) continue;
      if (restrictedTargetTeamId && partnerTeam.rosterId !== restrictedTargetTeamId) continue;
      const partnerPlayers = partnerTeam.players.filter(
        (player) =>
          isTradeablePlayerAsset(player) &&
          (player.value ?? 0) >=
            (isSpecificTargetTeam ? Math.max(0, minimumRecommendedPieceValue - 500) : minimumRecommendedPieceValue)
      );

      if (findTradeDirection === "up") {
        const targetPlayers = partnerPlayers.filter((player) => {
          if (player.position !== findTradeTargetPosition) return false;
          const targetValue = player.value ?? 0;
          const baseValue = selectedFindTradePlayer.value ?? 0;
          return targetValue > baseValue + (isSpecificTargetTeam ? 0 : 50);
        });

        for (const targetPlayer of targetPlayers) {
          for (const extraPlayer of myTeamAssets) {
            const sendAssets = [selectedFindTradePlayer, extraPlayer];
            const receiveAssets = [targetPlayer];
            const selectedValue = selectedFindTradePlayer.value ?? 0;
            const targetValue = targetPlayer.value ?? 0;
            const extraValue = extraPlayer.value ?? 0;
            const rawGap = Math.abs(targetValue - (selectedValue + extraValue));
            if (targetValue <= selectedValue) continue;
            if (extraValue > targetValue * (isSpecificTargetTeam ? 1.25 : 1.1)) continue;
            if (
              rawGap >
              Math.max(isSpecificTargetTeam ? 3200 : 2400, targetValue * (isSpecificTargetTeam ? 0.62 : 0.5))
            ) {
              continue;
            }

            const { valueAdjustment, favoredSide } = calculateKtcStyleAdjustedTotals(
              receiveAssets,
              sendAssets,
              undefined,
              overallMaxPlayerValue
            );
            const adjustedDelta = favoredSide === "receive" ? valueAdjustment : -valueAdjustment;
            if (Math.abs(adjustedDelta) > (isSpecificTargetTeam ? 2600 : 2000)) continue;

            recommendations.push({
              id: `up-${sourceTeam.rosterId}-${partnerTeam.rosterId}-${selectedFindTradePlayer.id}-${extraPlayer.id}-${targetPlayer.id}`,
              sourceTeamId: sourceTeam.rosterId,
              partnerTeamId: partnerTeam.rosterId,
              selectedPlayer: selectedFindTradePlayer,
              myAssets: sendAssets,
              partnerAssets: receiveAssets,
              adjustedDelta,
              balanceAddValue: findRecommendationBalanceAddValue(receiveAssets, sendAssets, overallMaxPlayerValue),
              explanation: `${selectedFindTradePlayer.name} plus ${extraPlayer.name} for a higher-value ${findTradeTargetPosition}.`,
            });
          }
        }
      } else {
        const targetPlayers = partnerPlayers.filter((player) => {
          if (player.position !== findTradeTargetPosition) return false;
          const targetValue = player.value ?? 0;
          const baseValue = selectedFindTradePlayer.value ?? 0;
          const valueDrop = baseValue - targetValue;
          return (
            valueDrop >= (isSpecificTargetTeam ? 0 : 50) &&
            valueDrop <=
              Math.max(
                isSpecificTargetTeam ? 3400 : isEliteSelectedPlayer ? 4600 : 2600,
                baseValue * (isSpecificTargetTeam ? 0.55 : isEliteSelectedPlayer ? 0.58 : 0.42)
              )
          );
        });

        for (const targetPlayer of targetPlayers) {
          for (const extraPlayer of partnerPlayers) {
            if (extraPlayer.id === targetPlayer.id) continue;
            const receiveAssets = [targetPlayer, extraPlayer];
            const sendAssets = [selectedFindTradePlayer];
            const targetValue = targetPlayer.value ?? 0;
            const extraValue = extraPlayer.value ?? 0;
            const selectedValue = selectedFindTradePlayer.value ?? 0;
            if (targetValue >= selectedValue) continue;
            if (extraValue > selectedValue * (isSpecificTargetTeam ? 1.05 : isEliteSelectedPlayer ? 1.2 : 0.95)) {
              continue;
            }
            if (
              targetValue + extraValue <
              selectedValue * (isSpecificTargetTeam ? 0.62 : isEliteSelectedPlayer ? 0.6 : 0.72)
            ) {
              continue;
            }
            if (
              targetValue + extraValue >
              selectedValue * (isSpecificTargetTeam ? 1.45 : isEliteSelectedPlayer ? 1.55 : 1.32)
            ) {
              continue;
            }

            const { valueAdjustment, favoredSide } = calculateKtcStyleAdjustedTotals(
              receiveAssets,
              sendAssets,
              undefined,
              overallMaxPlayerValue
            );
            const adjustedDelta = favoredSide === "receive" ? valueAdjustment : -valueAdjustment;
            if (Math.abs(adjustedDelta) > (isSpecificTargetTeam ? 2600 : isEliteSelectedPlayer ? 2600 : 2000)) {
              continue;
            }

            recommendations.push({
              id: `down-${sourceTeam.rosterId}-${partnerTeam.rosterId}-${selectedFindTradePlayer.id}-${targetPlayer.id}-${extraPlayer.id}`,
              sourceTeamId: sourceTeam.rosterId,
              partnerTeamId: partnerTeam.rosterId,
              selectedPlayer: selectedFindTradePlayer,
              myAssets: sendAssets,
              partnerAssets: receiveAssets,
              adjustedDelta,
              balanceAddValue: findRecommendationBalanceAddValue(receiveAssets, sendAssets, overallMaxPlayerValue),
              explanation: `${selectedFindTradePlayer.name} for a lower-value ${findTradeTargetPosition} plus another asset.`,
            });
          }
        }
      }
    }

    const bestRecommendationByTargetPlayerId = new Map<string, FindTradeRecommendation>();

    recommendations
      .sort((left, right) => {
        const balanceGap = left.balanceAddValue - right.balanceAddValue;
        if (balanceGap !== 0) return balanceGap;
        const deltaGap = Math.abs(left.adjustedDelta) - Math.abs(right.adjustedDelta);
        if (deltaGap !== 0) return deltaGap;
        const partnerValueGap = sumAssetValues(right.partnerAssets) - sumAssetValues(left.partnerAssets);
        if (partnerValueGap !== 0) return partnerValueGap;
        return left.partnerAssets[0]?.name.localeCompare(right.partnerAssets[0]?.name ?? "") ?? 0;
      })
      .forEach((recommendation) => {
        const targetPlayerId = recommendation.partnerAssets[0]?.id;
        if (!targetPlayerId) return;
        if (!bestRecommendationByTargetPlayerId.has(targetPlayerId)) {
          bestRecommendationByTargetPlayerId.set(targetPlayerId, recommendation);
        }
      });

    return Array.from(bestRecommendationByTargetPlayerId.values()).slice(0, 40);
  }, [
    findTradeDirection,
    findTradeTargetPosition,
    findTradeTargetTeamId,
    importedTeams,
    isNarrowViewport,
    overallMaxPlayerValue,
    selectedFindTradePlayer,
    selectedFindTradeTeam,
    singleOpenTeam,
  ]);

  const visibleFindTradeRecommendations = useMemo(
    () => findTradeRecommendations.slice(0, visibleFindTradeCount),
    [findTradeRecommendations, visibleFindTradeCount]
  );

  const hasMoreFindTradeRecommendations =
    visibleFindTradeRecommendations.length < findTradeRecommendations.length;

  const targetableTeams = useMemo(
    () =>
      importedTeams.filter(
        (team) => team.rosterId !== (isNarrowViewport ? selectedFindTradeTeam?.rosterId : singleOpenTeam?.rosterId)
      ),
    [importedTeams, isNarrowViewport, selectedFindTradeTeam?.rosterId, singleOpenTeam?.rosterId]
  );

  const selectedTargetTeam = useMemo(
    () => targetableTeams.find((team) => String(team.rosterId) === findTradeTargetTeamId) ?? null,
    [findTradeTargetTeamId, targetableTeams]
  );

  const mobileFindTradeCandidates = useMemo(() => {
    return importedTeams
      .flatMap((team) => team.players.filter(isTradeablePlayerAsset).map((player) => ({ team, player })))
      .sort((left, right) => {
        const valueGap =
          (right.player.value ?? Number.NEGATIVE_INFINITY) - (left.player.value ?? Number.NEGATIVE_INFINITY);
        if (valueGap !== 0) return valueGap;
        return left.player.name.localeCompare(right.player.name);
      });
  }, [importedTeams]);

  const mobileFindTradeResults = useMemo(() => {
    const query = mobileFindTradeQuery.trim().toLowerCase();
    const pool = mobileFindTradeCandidates;
    if (!query) {
      return pool.slice(0, 12);
    }
    return pool
      .filter(({ team, player }) => {
        const name = String(player.name ?? "").toLowerCase();
        const position = String(player.position ?? "").toLowerCase();
        const nflTeam = String(player.team ?? "").toLowerCase();
        const rosterName = String(team.displayName ?? "").toLowerCase();
        return (
          name.includes(query) ||
          position.includes(query) ||
          nflTeam.includes(query) ||
          rosterName.includes(query)
        );
      })
      .slice(0, 12);
  }, [mobileFindTradeCandidates, mobileFindTradeQuery]);

  useEffect(() => {
    setFindTradePlayerId(null);
    setFindTradeTargetPosition("");
    setFindTradeTargetTeamId("");
    setVisibleFindTradeCount(10);
    setMobileFindTradeQuery("");
  }, [resetKey]);

  useEffect(() => {
    const sourceTeam = isNarrowViewport ? selectedFindTradeTeam : singleOpenTeam;
    if (!sourceTeam) {
      setFindTradePlayerId(null);
      return;
    }
    if (!findTradePlayerId) return;
    const stillExists = sourceTeam.players.some((player) => player.id === findTradePlayerId);
    if (!stillExists) setFindTradePlayerId(null);
  }, [findTradePlayerId, isNarrowViewport, selectedFindTradeTeam, singleOpenTeam]);

  useEffect(() => {
    setVisibleFindTradeCount(10);
  }, [
    findTradeDirection,
    findTradePlayerId,
    findTradeTargetPosition,
    findTradeTargetTeamId,
    selectedFindTradeTeam?.rosterId,
    singleOpenTeam?.rosterId,
  ]);

  return {
    findTradeDirection,
    findTradePlayerId,
    findTradeRecommendations,
    findTradeTargetPosition,
    findTradeTargetTeamId,
    hasMoreFindTradeRecommendations,
    mobileFindTradeQuery,
    mobileFindTradeResults,
    playerLookupResults,
    selectedFindTradePlayer,
    selectedFindTradeTeam,
    selectedTargetTeam,
    setFindTradeDirection,
    setFindTradePlayerId,
    setFindTradeTargetPosition,
    setFindTradeTargetTeamId,
    setMobileFindTradeQuery,
    setVisibleFindTradeCount,
    targetableTeams,
    visibleFindTradeRecommendations,
  };
}
